<script lang="ts">
  import { SvelteSet } from "svelte/reactivity";
  import { untrack } from "svelte";
  import { modalDialog } from "@/actions/modalDialog";
  import ServerHeader from "@/components/ServerHeader.svelte";
  import ServerStateNotice from "@/components/ServerStateNotice.svelte";
  import { runServerAction, serverActions, type ServerAction } from "@/stores/dashboard";
  import { serverActionAvailability } from "@/server-state-policy";
  import { supportsPlugins, transitionalStatuses } from "@/config";
  import ServerNavigation from "@/components/ServerNavigation.svelte";
  import Select from "@/components/Select.svelte";
  import TextInput from "@/components/TextInput.svelte";
  import { get } from "svelte/store";
  import { api, type GameServer } from "@/api";
  import { routeHref } from "@/router";
  import { token as tokenStore } from "@/stores/session";
  import {
    dashboardRealtimeHealthy,
    subscribeDashboardInvalidations,
  } from "@/stores/dashboard-realtime";
  import { confirmModal } from "@/stores/modal";
  import { formatBytes, formatDate } from "@/utils/format";
  import { pluginDisplayName } from "@/utils/plugin-display-name";

  const { id }: { id: string } = $props();

  let serverInfo = $state<GameServer | null>(null);
  let serverInfoError = $state("");
  let serverCore = $state("");
  let serverVersion = $state("");
  let directory = $state("plugins");

  const extensionLabel = $derived(
    supportsPlugins(serverCore) ? "Плагины" : serverCore === "vanilla" ? "Расширения" : "Моды",
  );
  const extensionSingular = $derived(
    supportsPlugins(serverCore) ? "плагин" : serverCore === "vanilla" ? "расширение" : "мод",
  );
  const extensionGenitive = $derived(
    supportsPlugins(serverCore) ? "плагинов" : serverCore === "vanilla" ? "расширений" : "модов",
  );

  let installed = $state<Array<{ name: string; size: number; mtimeMs?: number }>>([]);
  let loadingInstalled = $state(true);
  let busy = $state<string | null>(null);
  let pageError = $state("");

  // Modal & Search state
  let showModal = $state(false);
  let searchQuery = $state("");
  let searchResults = $state<
    Array<{ id: string; name: string; description: string; slug: string; iconUrl: string | null }>
  >([]);
  let searchTotal = $state(0);
  let searchPage = $state(1);
  const pageSize = 8;
  let searching = $state(false);
  let modalError = $state("");
  let installNotice = $state("");
  let searchTimeout: ReturnType<typeof setTimeout> | null = null;
  let searchAbort: AbortController | null = null;

  // Versions and installing
  let versions = $state<
    Record<
      string,
      Array<{ id: string; name: string; gameVersions?: string[]; compatible?: boolean }>
    >
  >({});
  let loadingVersions = $state<string | null>(null);
  let selectedVersion = $state<Record<string, string>>({});
  let installingId = $state<string | null>(null);
  const justInstalledIds = new SvelteSet<string>();

  const extensionsLocked = $derived.by(() => {
    if (!serverInfo || serverInfo.status !== "stopped") {
      return true;
    }
    return false;
  });

  const totalPages = $derived(Math.max(1, Math.ceil(searchTotal / pageSize)));
  const actionStates = $derived({
    start: Boolean($serverActions[`start:${id}`]),
    restart: Boolean($serverActions[`restart:${id}`]),
    stop: Boolean($serverActions[`stop:${id}`]),
    kill: Boolean($serverActions[`kill:${id}`]),
  });

  function token(): string {
    const value = get(tokenStore);
    if (!value) {
      throw new Error("нет активной сессии");
    }
    return value;
  }

  function handleSearchKeydown(event: KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      void executeSearch(1);
    }
  }

  function clearSearch() {
    searchQuery = "";
    void executeSearch(1);
  }

  function prevSearchPage() {
    void executeSearch(searchPage - 1);
  }

  function nextSearchPage() {
    void executeSearch(searchPage + 1);
  }

  function handleRefreshInstalled() {
    void loadServerInfo();
    void loadInstalled();
  }

  async function loadServerInfo(signal?: AbortSignal): Promise<boolean> {
    const requestedId = id;
    try {
      const server = await api.server(token(), requestedId, signal);
      if (signal?.aborted || requestedId !== id) {
        return false;
      }
      serverInfo = server;
      serverInfoError = "";
      serverCore = server.core;
      serverVersion = server.version;
      return true;
    } catch {
      if (signal?.aborted || requestedId !== id) {
        return false;
      }
      serverInfoError =
        "Не удалось загрузить данные сервера. Повторите загрузку, чтобы проверить возможность установки расширений.";
      return false;
    }
  }

  async function loadInstalled() {
    const requestedId = id;
    loadingInstalled = true;
    pageError = "";
    try {
      const response = await api.plugins(token(), requestedId);
      if (requestedId !== id) {
        return;
      }
      installed = response.entries;
      directory = response.directory;
      if (!serverCore) {
        serverCore = response.directory === "plugins" ? "paper" : "fabric";
      }
    } catch (requestError) {
      if (requestedId === id) {
        pageError = (requestError as Error).message;
      }
    } finally {
      if (requestedId === id) {
        loadingInstalled = false;
      }
    }
  }

  async function remove(filename: string) {
    if (extensionsLocked || busy !== null) {
      return;
    }
    const displayName = pluginDisplayName(filename);
    if (
      !(await confirmModal(
        `Удалить ${displayName}?`,
        `Вы уверены, что хотите удалить файл ${filename}?`,
        "Удалить",
      ))
    ) {
      return;
    }
    busy = filename;
    pageError = "";
    try {
      await api.deletePlugin(token(), id, filename);
      justInstalledIds.clear();
      await loadInstalled();
    } catch (requestError) {
      pageError = (requestError as Error).message;
    } finally {
      busy = null;
    }
  }

  async function executeSearch(page = 1) {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    searchAbort?.abort();
    const controller = new AbortController();
    searchAbort = controller;
    searching = true;
    modalError = "";
    searchPage = page;
    const offset = (page - 1) * pageSize;
    try {
      const response = await api.pluginSearch(
        token(),
        id,
        searchQuery.trim(),
        "modrinth",
        offset,
        pageSize,
        controller.signal,
      );
      if (controller.signal.aborted) {
        return;
      }
      searchResults = response.results;
      searchTotal = response.total ?? response.results.length;
    } catch (requestError) {
      if ((requestError as Error).name === "AbortError" || controller.signal.aborted) {
        return;
      }
      modalError = (requestError as Error).message;
    } finally {
      if (!controller.signal.aborted) {
        searching = false;
      }
    }
  }

  function onSearchInput() {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    searchTimeout = setTimeout(() => {
      void executeSearch(1);
    }, 300);
  }

  async function handleServerAction(action: ServerAction) {
    if (!serverInfo || !serverActionAvailability(serverInfo)[action]) {
      return;
    }
    const currentToken = get(tokenStore);
    const requestedId = id;
    if (
      action === "kill" &&
      !(await confirmModal(
        "Принудительно остановить сервер?",
        "Сервер будет остановлен немедленно. Несохранённые данные могут быть потеряны.",
        "Убить сервер",
      ))
    ) {
      return;
    }
    if (
      !serverInfo ||
      id !== requestedId ||
      serverInfo.id !== requestedId ||
      get(tokenStore) !== currentToken ||
      !serverActionAvailability(serverInfo)[action]
    ) {
      return;
    }
    if (await runServerAction(serverInfo, action)) {
      await loadServerInfo();
    }
  }

  $effect(() => {
    const currentId = id;
    serverInfo = null;
    serverInfoError = "";
    serverCore = "";
    const controller = new AbortController();
    let stopped = false;
    let hidden = document.visibilityState === "hidden";
    let dirty = false;
    let failures = 0;
    let flight: Promise<void> | null = null;
    let timer: number | undefined;
    const clearTimer = () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
      timer = undefined;
    };
    const schedule = () => {
      clearTimer();
      if (stopped || hidden || flight) {
        return;
      }
      const base = untrack(() =>
        serverInfo && transitionalStatuses.has(serverInfo.status)
          ? 4_000
          : get(dashboardRealtimeHealthy)
            ? 120_000
            : 30_000,
      );
      const delay = Math.min(120_000, base * 2 ** Math.min(failures, 5));
      timer = window.setTimeout(
        () => void request().catch(() => {}),
        Math.round(delay * (1 + Math.random() * 0.1)),
      );
    };
    const request = (): Promise<void> => {
      if (stopped) {
        return Promise.resolve();
      }
      dirty = true;
      clearTimer();
      if (hidden) {
        return flight ?? Promise.resolve();
      }
      if (flight) {
        return flight;
      }
      flight = (async () => {
        while (dirty && !stopped && !hidden) {
          dirty = false;
          if (await loadServerInfo(controller.signal)) {
            failures = 0;
          } else if (!controller.signal.aborted) {
            failures = Math.min(failures + 1, 5);
          }
        }
      })().finally(() => {
        flight = null;
        schedule();
      });
      return flight;
    };
    const unsubscribe = subscribeDashboardInvalidations((event) => {
      if (event.allServers || event.serverIds?.includes(currentId)) {
        return request();
      }
    });
    const visibilityChanged = () => {
      const nextHidden = document.visibilityState === "hidden";
      if (nextHidden === hidden) {
        return;
      }
      hidden = nextHidden;
      if (hidden) {
        dirty = false;
        clearTimer();
      } else {
        void request().catch(() => {});
      }
    };
    const unhealth = dashboardRealtimeHealthy.subscribe(() => schedule());
    document.addEventListener("visibilitychange", visibilityChanged);
    void request().catch(() => {});
    return () => {
      stopped = true;
      clearTimer();
      controller.abort();
      unsubscribe();
      unhealth();
      document.removeEventListener("visibilitychange", visibilityChanged);
    };
  });

  function openAddModal() {
    if (extensionsLocked || installingId !== null) {
      return;
    }
    installNotice = "";
    showModal = true;
    modalError = "";
    searchQuery = "";
    searchPage = 1;
    justInstalledIds.clear();
    void loadInstalled();
    void executeSearch(1);
  }

  function closeModal() {
    if (installingId !== null) {
      return;
    }
    showModal = false;
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    searchAbort?.abort();
  }

  async function loadItemVersions(projectId: string) {
    if (loadingVersions === projectId || versions[projectId]) {
      return;
    }
    loadingVersions = projectId;
    try {
      const response = await api.pluginVersions(token(), id, projectId, "modrinth", true);
      versions = { ...versions, [projectId]: response.versions };
      if (response.versions.length > 0 && !selectedVersion[projectId]) {
        selectedVersion = { ...selectedVersion, [projectId]: response.versions[0].id };
      }
    } catch (requestError) {
      modalError = (requestError as Error).message;
    } finally {
      loadingVersions = null;
    }
  }

  async function install(item: { id: string; name: string }) {
    if (extensionsLocked || installingId !== null || busy !== null) {
      return;
    }
    installNotice = "";
    installingId = item.id;
    modalError = "";
    try {
      let targetVersionId = selectedVersion[item.id];
      if (!targetVersionId) {
        let currentVersions = versions[item.id];
        if (!currentVersions) {
          const response = await api.pluginVersions(token(), id, item.id, "modrinth", true);
          currentVersions = response.versions;
          versions = { ...versions, [item.id]: currentVersions };
        }
        if (!currentVersions || currentVersions.length === 0) {
          throw new Error("Совместимых версий для текущего сервера не найдено");
        }
        targetVersionId = currentVersions[0].id;
        selectedVersion = { ...selectedVersion, [item.id]: targetVersionId };
      }

      const currentVersions = versions[item.id] || [];
      const isCompatible =
        currentVersions.find((v) => v.id === targetVersionId)?.compatible !== false;
      if (!isCompatible) {
        if (
          !(await confirmModal(
            "Установить несовместимую версию?",
            `Версия может быть несовместима с ядром сервера (${serverVersion}). Вы уверены?`,
            "Установить принудительно",
          ))
        ) {
          return;
        }
      }

      const force = !isCompatible;
      await api.installPlugin(token(), id, item.id, targetVersionId, "modrinth", force);
      installNotice = `${item.name}: расширение установлено. ${serverInfo?.status === "running" ? "Остановите и запустите сервер, чтобы загрузить расширение." : "Сервер загрузит расширение при следующем запуске."}`;
      justInstalledIds.add(item.id);
      await loadInstalled();
    } catch (requestError) {
      modalError = (requestError as Error).message;
    } finally {
      installingId = null;
    }
  }

  function isItemInstalled(item: { id: string; name: string; slug: string }): boolean {
    if (justInstalledIds.has(item.id)) {
      return true;
    }
    const slugNorm = item.slug.toLowerCase().replace(/[^a-z0-9]/g, "");
    const nameNorm = item.name.toLowerCase().replace(/[^a-z0-9]/g, "");
    return installed.some((entry) => {
      const fileNorm = entry.name.toLowerCase().replace(/[^a-z0-9]/g, "");
      return (
        (slugNorm.length > 2 && fileNorm.includes(slugNorm)) ||
        (nameNorm.length > 3 && fileNorm.includes(nameNorm))
      );
    });
  }

  $effect(() => {
    void id;
    justInstalledIds.clear();
    void loadInstalled();
  });
</script>

<section class="plugins-page">
  {#if serverInfo}
    <ServerHeader server={serverInfo} busyActions={actionStates} onAction={handleServerAction} />
    <ServerStateNotice
      server={serverInfo}
      retryBusy={Object.values(actionStates).some(Boolean)}
      onRetry={() => void handleServerAction("start")}
    />
  {:else}
    <header class="dashboard-intro">
      <div>
        <a class="back-link" href={routeHref("server-home", { id })}>← Сервер</a>
        <h1>{extensionLabel}</h1>
      </div>
    </header>
  {/if}

  <ServerNavigation {id} core={serverCore} section="plugins" />

  {#if serverInfoError}
    <div class="form-error" role="alert">
      <p>{serverInfoError}</p>
      <button
        class="ghost-button"
        type="button"
        onclick={handleRefreshInstalled}
        disabled={loadingInstalled}>Повторить загрузку данных сервера</button
      >
    </div>
  {/if}

  {#if pageError}
    <div class="form-error" role="alert">{pageError}</div>
  {/if}

  {#if serverCore === "vanilla"}
    <section class="panel-card empty-card">
      <p class="eyebrow">Не поддерживается</p>
      <h2>Ядро Vanilla</h2>
      <p class="settings-note">
        Vanilla не поддерживает плагины и моды. Для них нужна сборка Paper, Leaf или Fabric. Смена
        сборки в «Настройках» удалит мир и остальные файлы — сначала создайте резервную копию.
      </p>
      <a class="button" href={routeHref("server-settings", { id })}>Перейти в настройки</a>
    </section>
  {:else}
    <section class="panel-card installed-panel">
      <header class="panel-heading">
        <div>
          <p class="eyebrow">Каталог /{directory}</p>
          <h2>Установленные {extensionLabel.toLowerCase()}</h2>
        </div>
        <div class="heading-controls">
          <span class="panel-count">{installed.length}</span>
          <button
            class="ghost-button refresh-btn"
            type="button"
            onclick={handleRefreshInstalled}
            disabled={loadingInstalled}
            title="Обновить список"
          >
            {loadingInstalled ? "Обновление…" : "Обновить"}
          </button>
          <button
            class="button add-btn"
            type="button"
            onclick={openAddModal}
            disabled={extensionsLocked || loadingInstalled}
          >
            + Добавить
          </button>
        </div>
      </header>

      {#if serverInfo?.status === "running"}
        <p class="settings-note">
          Остановите сервер, чтобы устанавливать и удалять {extensionLabel.toLowerCase()}.
        </p>
      {/if}

      {#if loadingInstalled}
        <div class="list-loading">
          <span class="loading-orbit"></span>
          <p>Загрузка списка установленных {extensionGenitive}…</p>
        </div>
      {:else if installed.length === 0}
        <div class="empty-state">
          <p class="empty-title">Установленных {extensionGenitive} пока нет</p>
          <p class="empty-desc">
            Нажмите кнопку «Добавить», чтобы найти и установить подходящие {extensionLabel.toLowerCase()}
            из каталога.
          </p>
          <button
            class="button add-btn"
            type="button"
            onclick={openAddModal}
            disabled={extensionsLocked || loadingInstalled}
          >
            + Добавить {extensionSingular}
          </button>
        </div>
      {:else}
        <div class="installed-table" role="list">
          {#each installed as item (item.name)}
            <article class="installed-row" role="listitem">
              <div class="item-primary">
                <span class="item-badge" aria-hidden="true">JAR</span>
                <div class="item-info">
                  <strong class="item-name" title={item.name}>{pluginDisplayName(item.name)}</strong
                  >
                  <div class="item-meta">
                    <span class="meta-filename" title={item.name}>{item.name}</span>
                    <span class="meta-divider">·</span>
                    <span class="meta-size">{formatBytes(item.size)}</span>
                    {#if item.mtimeMs}
                      <span class="meta-divider">·</span>
                      <span class="meta-date">{formatDate(item.mtimeMs)}</span>
                    {/if}
                  </div>
                </div>
              </div>
              <div class="item-actions">
                <button
                  class="danger-button"
                  type="button"
                  onclick={() => void remove(item.name)}
                  disabled={busy !== null || extensionsLocked}
                >
                  {busy === item.name ? "Удаляем…" : "Удалить"}
                </button>
              </div>
            </article>
          {/each}
        </div>
      {/if}
    </section>
  {/if}
</section>

{#if showModal}
  <dialog
    class="modal-dialog"
    use:modalDialog={{
      onDismiss: closeModal,
      canDismiss: () => installingId === null,
      initialFocus: "input",
    }}
    aria-labelledby="modal-search-title"
  >
    <header class="modal-header">
      <div>
        <span class="eyebrow">Каталог Modrinth</span>
        <h2 id="modal-search-title">Добавить {extensionSingular}</h2>
      </div>
      <button
        class="modal-close"
        type="button"
        onclick={closeModal}
        disabled={installingId !== null}
        aria-label="Закрыть">✕</button
      >
    </header>

    <div class="modal-search-box">
      <div class="search-input-wrap">
        <svg
          class="search-icon"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <circle cx="9" cy="9" r="6" stroke-width="1.8" />
          <path d="M13.5 13.5L18 18" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        <TextInput
          density="comfortable"
          leadingIcon
          trailingAction
          flexible
          aria-label="Поиск расширений"
          bind:value={searchQuery}
          oninput={onSearchInput}
          onkeydown={handleSearchKeydown}
          placeholder="Введите название для поиска..."
        />
        {#if searchQuery}
          <button class="clear-input-btn" type="button" onclick={clearSearch} aria-label="Очистить">
            ✕
          </button>
        {/if}
      </div>
    </div>

    {#if installNotice}<p class="install-notice" role="status">{installNotice}</p>{/if}
    {#if modalError}
      <div class="form-error modal-error" role="alert">{modalError}</div>
    {/if}

    <div class="modal-results-wrap">
      {#if searching}
        <div class="results-loading">
          <span class="loading-orbit"></span>
          <p>Поиск в каталоге…</p>
        </div>
      {:else if searchResults.length === 0}
        <div class="results-empty">
          {#if searchQuery.trim()}
            <p>По запросу <strong>«{searchQuery}»</strong> ничего не найдено.</p>
            <small>Попробуйте изменить поисковую фразу.</small>
          {:else}
            <p>Каталог пуст или не вернул результатов.</p>
          {/if}
        </div>
      {:else}
        <div class="results-list">
          {#each searchResults as item (item.id)}
            {@const installedAlready = isItemInstalled(item)}
            {@const isInstalling = installingId === item.id}
            {@const itemVersions = versions[item.id]}

            <article class="result-card">
              <div class="result-icon-col">
                {#if item.iconUrl}
                  <img class="result-icon" src={item.iconUrl} alt={item.name} loading="lazy" />
                {:else}
                  <div class="result-icon-fallback" aria-hidden="true">
                    {item.name.slice(0, 2).toUpperCase()}
                  </div>
                {/if}
              </div>

              <div class="result-main">
                <div class="result-header">
                  <strong class="result-title">{item.name}</strong>
                  {#if installedAlready}
                    <span class="installed-tag">Установлен</span>
                  {/if}
                </div>
                {#if item.description}
                  <p class="result-desc">{item.description}</p>
                {/if}

                {#if itemVersions && itemVersions.length > 1}
                  <div class="version-select-wrap">
                    <div class="version-select-label">
                      <span>Версия:</span>
                      <Select
                        aria-label="Версия расширения"
                        bind:value={selectedVersion[item.id]}
                        disabled={installingId !== null}
                        options={itemVersions.map((version) => ({
                          value: version.id,
                          label: `${version.name || version.id}${version.compatible === false ? " (несовместимо)" : ""}`,
                        }))}
                      />
                    </div>
                  </div>
                {/if}
              </div>

              <div class="result-action">
                {#if !itemVersions && !installedAlready}
                  <button
                    class="ghost-button version-toggle-btn"
                    type="button"
                    onclick={() => void loadItemVersions(item.id)}
                    disabled={loadingVersions === item.id || installingId !== null}
                  >
                    {loadingVersions === item.id ? "…" : "Версии"}
                  </button>
                {/if}
                <button
                  class="button install-btn"
                  type="button"
                  onclick={() => void install(item)}
                  disabled={installingId !== null || busy !== null || extensionsLocked}
                >
                  {#if isInstalling}
                    Установка…
                  {:else if installedAlready}
                    Обновить
                  {:else}
                    Установить
                  {/if}
                </button>
              </div>
            </article>
          {/each}
        </div>
      {/if}
    </div>

    {#if searchTotal > pageSize}
      <footer class="modal-pagination">
        <button
          class="ghost-button page-btn"
          type="button"
          disabled={searchPage <= 1 || searching}
          onclick={prevSearchPage}
        >
          ← Назад
        </button>
        <span class="page-indicator">
          Страница {searchPage} из {totalPages}
          <small class="page-total">({searchTotal})</small>
        </span>
        <button
          class="ghost-button page-btn"
          type="button"
          disabled={searchPage >= totalPages || searching}
          onclick={nextSearchPage}
        >
          Вперёд →
        </button>
      </footer>
    {/if}
  </dialog>
{/if}

<style>
  .install-notice {
    margin: 0;
    padding: 1rem 1.5rem;
    color: var(--green);
    background: rgb(154 189 143 / 0.06);
    font-size: 0.9rem;
    line-height: 1.5;
  }

  .plugins-page {
    min-width: 0;
  }

  .installed-panel {
    border: 1px solid var(--line);
    background: var(--surface);
  }

  .heading-controls {
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  .refresh-btn {
    padding: 0.45rem 0.85rem;
    font-size: 0.72rem;
  }

  .add-btn {
    padding: 0.55rem 1.1rem;
    font-size: 0.75rem;
  }

  .list-loading {
    display: grid;
    min-height: 12rem;
    place-content: center;
    justify-items: center;
    gap: 0.8rem;
    padding: 2rem;
    color: var(--muted);
    font-size: 0.85rem;
  }

  .empty-state,
  .empty-card {
    display: grid;
    place-content: center;
    justify-items: center;
    gap: 0.8rem;
    padding: 3.5rem 1.5rem;
    text-align: center;
  }

  .empty-title {
    margin: 0;
    color: var(--cream);
    font: 600 1.1rem/1.2 var(--font-display);
  }

  .empty-desc {
    max-width: 28rem;
    margin: 0;
    color: var(--muted);
    font-size: 0.85rem;
    line-height: 1.5;
  }

  .installed-table {
    display: grid;
    border-top: 1px solid var(--line);
  }

  .installed-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.2rem;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--line);
    background: rgb(241 225 198 / 0.015);
    transition: background 140ms ease;
  }

  .installed-row:hover {
    background: rgb(241 225 198 / 0.04);
  }

  .item-primary {
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 1rem;
  }

  .item-badge {
    display: grid;
    width: 2.5rem;
    height: 2.5rem;
    flex-shrink: 0;
    place-items: center;
    border: 1px solid var(--line-strong);
    border-radius: 0.25rem;
    color: var(--mars-light);
    background: rgb(214 75 50 / 0.12);
    font: 700 0.62rem/1 var(--font-display);
    letter-spacing: 0.05em;
  }

  .item-info {
    display: grid;
    min-width: 0;
    gap: 0.25rem;
  }

  .item-name {
    overflow: hidden;
    color: var(--white);
    font: 600 0.95rem/1.2 var(--font-body);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta-filename {
    overflow: hidden;
    color: var(--dim);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.7rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 22rem;
  }

  .item-meta {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    color: var(--muted);
    font-size: 0.72rem;
  }

  .meta-divider {
    color: var(--dim);
  }

  .item-actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.6rem;
  }

  .danger-button {
    padding: 0.45rem 0.85rem;
    border: 1px solid rgb(214 75 50 / 0.35);
    border-radius: 2px;
    color: var(--mars-light);
    background: rgb(214 75 50 / 0.08);
    font-size: 0.72rem;
    transition:
      background 140ms ease,
      border-color 140ms ease;
  }

  .danger-button:hover:not(:disabled) {
    border-color: var(--mars-light);
    color: #fff;
    background: var(--mars);
  }

  /* Modal */
  .modal-dialog::backdrop {
    background: rgb(8 7 6 / 78%);
    backdrop-filter: blur(6px);
  }

  .modal-dialog {
    position: fixed;
    inset: 0;
    display: flex;
    width: min(calc(100% - 2rem), 54rem);
    max-height: min(calc(100dvh - 2rem), 48rem);
    min-width: 0;
    flex-direction: column;
    margin: auto;
    padding: 0;
    overflow: hidden;
    border: 1px solid var(--line-strong);
    border-radius: 2px;
    color: var(--cream);
    background: var(--surface-raised);
    box-shadow: 0 1.5rem 5rem rgb(0 0 0 / 55%);
  }

  .modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid var(--line);
    background: var(--surface);
  }

  .modal-header h2 {
    margin: 0.4rem 0 0;
    font: 500 1.35rem/1 var(--font-display);
    text-transform: uppercase;
  }

  .modal-close {
    width: 2rem;
    height: 2rem;
    display: grid;
    place-items: center;
    padding: 0;
    border: 1px solid var(--line);
    border-radius: 2px;
    color: var(--muted);
    background: transparent;
    font-size: 0.9rem;
    transition:
      color 140ms ease,
      border-color 140ms ease;
  }

  .modal-close:hover {
    border-color: var(--line-strong);
    color: var(--white);
  }

  .modal-search-box {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--line);
    background: var(--ink-deep);
  }

  .search-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }

  .search-icon {
    position: absolute;
    left: 0.85rem;
    width: 1.1rem;
    height: 1.1rem;
    color: var(--dim);
    pointer-events: none;
  }

  .clear-input-btn {
    position: absolute;
    right: 0.65rem;
    width: 1.5rem;
    height: 1.5rem;
    display: grid;
    place-items: center;
    padding: 0;
    border: 0;
    color: var(--dim);
    background: transparent;
    font-size: 0.75rem;
  }

  .clear-input-btn:hover {
    color: var(--cream);
  }

  .modal-error {
    margin: 0.75rem 1.5rem 0;
  }

  .modal-results-wrap {
    flex: 1;
    overflow-y: auto;
    min-height: 18rem;
  }

  .results-loading,
  .results-empty {
    display: grid;
    min-height: 18rem;
    place-content: center;
    justify-items: center;
    gap: 0.6rem;
    padding: 2rem;
    color: var(--muted);
    text-align: center;
    font-size: 0.88rem;
  }

  .results-empty strong {
    color: var(--white);
  }

  .results-empty small {
    color: var(--dim);
  }

  .results-list {
    display: grid;
  }

  .result-card {
    display: flex;
    align-items: flex-start;
    gap: 1.2rem;
    padding: 1.1rem 1.5rem;
    border-bottom: 1px solid var(--line);
    transition: background 140ms ease;
  }

  .result-card:hover {
    background: rgb(241 225 198 / 0.03);
  }

  .result-icon-col {
    flex-shrink: 0;
  }

  .result-icon {
    width: 3.2rem;
    height: 3.2rem;
    border-radius: 0.35rem;
    object-fit: cover;
    background: var(--ink);
  }

  .result-icon-fallback {
    display: grid;
    width: 3.2rem;
    height: 3.2rem;
    place-items: center;
    border: 1px solid var(--line);
    border-radius: 0.35rem;
    color: var(--muted);
    background: var(--ink);
    font: 700 0.85rem/1 var(--font-display);
  }

  .result-main {
    display: grid;
    min-width: 0;
    flex: 1;
    gap: 0.35rem;
  }

  .result-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.6rem;
  }

  .result-title {
    color: var(--white);
    font-size: 0.95rem;
  }

  .installed-tag {
    padding: 0.15rem 0.45rem;
    border: 1px solid rgb(154 189 143 / 0.4);
    border-radius: 2px;
    color: var(--green);
    background: rgb(154 189 143 / 0.1);
    font: 600 0.62rem/1 var(--font-display);
    text-transform: uppercase;
  }

  .result-desc {
    margin: 0;
    color: var(--muted);
    font-size: 0.78rem;
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .version-select-wrap {
    margin-top: 0.3rem;
  }

  .version-select-label {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--dim);
    font-size: 0.72rem;
  }

  .result-action {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    align-items: flex-end;
    gap: 0.5rem;
    margin-top: 0.1rem;
  }

  .version-toggle-btn {
    padding: 0.3rem 0.6rem;
    font-size: 0.68rem;
    color: var(--dim);
  }

  .install-btn {
    padding: 0.45rem 1rem;
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .modal-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.85rem 1.5rem;
    border-top: 1px solid var(--line);
    background: var(--surface);
  }

  .page-btn {
    padding: 0.4rem 0.85rem;
    font-size: 0.72rem;
  }

  .page-indicator {
    color: var(--muted);
    font-size: 0.78rem;
  }

  .page-total {
    color: var(--dim);
  }

  @media (max-width: 640px) {
    .panel-heading {
      flex-direction: column;
      align-items: flex-start;
    }
    .panel-heading > div {
      min-width: 0;
      max-width: 100%;
    }
    .panel-heading h2 {
      overflow-wrap: anywhere;
    }
    .heading-controls {
      flex-wrap: wrap;
    }

    .installed-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.8rem;
    }

    .item-actions {
      width: 100%;
      justify-content: flex-end;
    }

    .result-card {
      flex-direction: column;
      gap: 0.8rem;
    }

    .result-action {
      width: 100%;
      flex-direction: row;
      justify-content: flex-end;
    }
  }
</style>
